'use client';

import type {ReactNode} from 'react';
import {createContext, useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';

import {useTranslation} from 'react-i18next';

import type {User} from 'types/payload-types';

interface PayloadFieldError {
    message: string;
    path: string;
}

interface PayloadError {
    data?: {errors?: PayloadFieldError[]};
    message: string;
}

interface PayloadErrorResponse {
    errors?: PayloadError[];
}

/**
 * Extrahiert die relevanteste Fehlermeldung aus einer Payload API-Response.
 * Bevorzugt verschachtelte Feld-Validierungsfehler gegenüber der äußeren Message.
 *
 * @param data Die geparste Payload-Fehlerantwort.
 * @returns Die Fehlermeldung oder undefined.
 */
const extractPayloadError = (data: PayloadErrorResponse): string | undefined => {
    const topError = data.errors?.[0];

    if (!topError) return undefined;

    // Verschachtelte Validierungsfehler bevorzugen (z.B. "E-Mail bereits registriert").
    const fieldError = topError.data?.errors?.[0]?.message;

    return fieldError ?? topError.message;
};

interface AuthContextValue {
    /** Fügt einen Walk zur Bibliothek des angemeldeten Users hinzu. */
    addToLibrary(walkId: string): Promise<void>;
    /**
     * Ob der letzte Auth-Check am Netzwerk gescheitert ist, etwa weil offline, statt eine
     * bestätigte Antwort vom Server zu erhalten. Anders als `isAuthenticated: false`
     * bedeutet dies NICHT "nicht eingeloggt", sondern "Status unbekannt" — Aufrufer, die
     * offline weiter funktionieren müssen, etwa bei bereits heruntergeladenen Walks, können
     * das nutzen, um diesen Fall von einem echten Logout zu unterscheiden.
     */
    authCheckFailed: boolean;
    /** Gibt an, ob ein Payload-Admin eingeloggt ist (hat Zugriff auf alle Walks). */
    isAdmin: boolean;
    /** Gibt an, ob der User authentifiziert ist. */
    isAuthenticated: boolean;
    /** Prüft ob ein Walk in der Bibliothek des Users ist. */
    isInLibrary(walkId: string): boolean;
    /** Gibt an, ob der Auth-Status noch geladen wird. */
    isLoading: boolean;
    /** Meldet den User über Payload CMS an. */
    login(email: string, password: string): Promise<void>;
    /** Meldet den User ab und löscht das Cookie. */
    logout(): Promise<void>;
    /** Aktualisiert den User-State vom Server. */
    refreshUser(): Promise<void>;
    /** Registriert einen neuen User und meldet ihn an. */
    register(email: string, name: string, password: string, locale: string, newsletterConsent?: boolean): Promise<void>;
    /** Entfernt einen Walk aus der Bibliothek des Users. */
    removeFromLibrary(walkId: string): Promise<void>;
    /**
     * Widerspricht der Verknüpfung von Analyse-Sessions (Umami) mit dem Konto (Art. 21 DSGVO),
     * oder hebt den Widerspruch wieder auf. Greift erst bei der nächsten Anmeldung – eine bereits
     * in der aktuellen Sitzung verknüpfte Umami-Session lässt sich nicht rückwirkend trennen.
     */
    setAnalyticsOptOut(optOut: boolean): Promise<void>;
    /** Ändert den Anzeigenamen des aktuell angemeldeten Users. */
    updateName(name: string): Promise<void>;
    /** Der aktuell angemeldete User oder null. */
    user: User | null;
}

const AuthContext = createContext<AuthContextValue | null>(null);

AuthContext.displayName = 'AuthContext';

/**
 * Extrahiert die Walk-IDs aus dem Library-Array des Users.
 *
 * @param library Das Library-Array (Walk-Objekte oder IDs).
 * @returns Array von Walk-ID-Strings.
 */
const getLibraryIds = (library: User['library']): string[] => {
    if (!library) return [];

    return library.map(entry => (typeof entry === 'string' ? entry : entry.id));
};

/**
 * Interner Hook der den kompletten Auth-State und alle Methoden bereitstellt.
 * Wird ausschließlich vom AuthProvider verwendet.
 *
 * Admin-Panel-Sessions (admin-users) werden NICHT als Frontend-Authentifizierung gewertet.
 * Nur explizit eingeloggte Frontend-User (users Collection) gelten als authentifiziert.
 *
 * @returns AuthContextValue mit User-State und Auth-Methoden.
 */
const useAuthProvider = (): AuthContextValue => {
    const {i18n, t} = useTranslation();
    const [user, setUser] = useState<User | null>(null);
    const [isAdmin, setIsAdmin] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [authCheckFailed, setAuthCheckFailed] = useState(false);
    const hasSentInitialIdentify = useRef(false);

    const refreshUser = useCallback(async () => {
        try {
            const res = await fetch('/api/users/me', {credentials: 'include'});

            // The network request itself succeeded (even if the response is a 401/etc.),
            // so this is a confirmed result, not a network failure.
            setAuthCheckFailed(false);

            if (res.ok) {
                const data = await res.json() as {user: User | null};

                if (data.user) {
                    setUser(data.user);
                    setIsAdmin(false);

                    return;
                }
            }

            // Kein Frontend-User gefunden – prüfe ob ein Admin-Token existiert
            // (damit wir den Logout-Button anzeigen können).
            try {
                const adminRes = await fetch('/api/admin-users/me', {credentials: 'include'});

                if (adminRes.ok) {
                    const adminData = await adminRes.json() as {user: unknown};

                    if (adminData.user) {
                        // Admin ist eingeloggt, aber NICHT als Frontend-User authentifiziert.
                        setUser(null);
                        setIsAdmin(true);

                        return;
                    }
                }
            } catch {
                // Ignore – kein Admin-Zugriff
            }

            setUser(null);
            setIsAdmin(false);
        } catch {
            // Der Request selbst ist gescheitert (z.B. offline) — der Auth-Status ist
            // damit unbekannt, NICHT bestätigt negativ. `user` bewusst unverändert lassen,
            // statt es auf null zu setzen: eine bereits vorhandene, zuvor bestätigte
            // Session soll durch einen einzelnen Netzwerkausfall nicht verworfen werden.
            setAuthCheckFailed(true);
        }
    }, []);

    useEffect(() => {
        void (async () => {
            try {
                await refreshUser();
            } finally {
                setIsLoading(false);
            }
        })();
    }, [refreshUser]);

    // Einmal pro Sitzung, sobald der initiale Auth-Check durch ist: Login-Status als Session-Property
    // mitschicken, unabhängig davon ob die Person schon vor diesem Seitenaufruf eingeloggt war
    // (Cookie) oder anonym bleibt. `login()`/`logout()` aktualisieren `isLoggedIn` danach bei Bedarf
    // erneut. Kein `id` hier – das bleibt `login()` vorbehalten und respektiert dort den Analyse-Opt-out.
    useEffect(() => {
        if (isLoading || hasSentInitialIdentify.current) return;

        hasSentInitialIdentify.current = true;
        window.umami?.identify({isLoggedIn: user !== null});
    }, [isLoading, user]);

    const login = useCallback(async (email: string, password: string) => {
        const res = await fetch('/api/users/login', {
            body: JSON.stringify({
                email,
                password
            }),
            credentials: 'include',
            headers: {
                'Accept-Language': i18n.language,
                'Content-Type': 'application/json'
            },
            method: 'POST'
        });

        if (!res.ok) {
            const data = await res.json() as PayloadErrorResponse;

            throw new Error(extractPayloadError(data) ?? t('login.error'));
        }

        const {user: loggedInUser} = await res.json() as {user?: User};

        if (loggedInUser) {
            // `isLoggedIn` ist nicht kontobezogen und läuft daher unabhängig vom Analyse-Opt-out.
            // Die Payload-User-ID hängen wir nur zusätzlich an, um eingeloggtes Verhalten über
            // Geräte/Sessions hinweg auswertbar zu machen – nur die ID, keine E-Mail o.ä., und nur
            // wenn dem nicht widersprochen wurde (analyticsOptOut, siehe Einstellungen).
            window.umami?.identify({
                isLoggedIn: true,
                ...(loggedInUser.analyticsOptOut ? {} : {id: loggedInUser.id})
            });
        }

        await refreshUser();
    }, [i18n.language, refreshUser, t]);

    const register = useCallback(async (
        email: string,
        name: string,
        password: string,
        locale: string,
        newsletterConsent?: boolean
    ) => {
        const res = await fetch(`/api/users?locale=${locale}`, {
            body: JSON.stringify({
                email,
                name,
                newsletterConsent: newsletterConsent ?? false,
                password
            }),
            credentials: 'include',
            headers: {
                'Accept-Language': locale,
                'Content-Type': 'application/json'
            },
            method: 'POST'
        });

        if (!res.ok) {
            const data = await res.json() as PayloadErrorResponse;

            throw new Error(extractPayloadError(data) ?? t('register.error'));
        }
    }, [t]);

    const logout = useCallback(async () => {
        // Beide Logout-Endpoints aufrufen um den Cookie sicher zu löschen,
        // unabhängig davon ob er zu users oder admin-users gehört.
        await Promise.allSettled([
            fetch('/api/users/logout', {
                credentials: 'include',
                method: 'POST'
            }),
            fetch('/api/admin-users/logout', {
                credentials: 'include',
                method: 'POST'
            })
        ]);
        setUser(null);
        setIsAdmin(false);

        // Aktualisiert `isLoggedIn` für den Rest der Sitzung. Löst NICHT rückwirkend die zuvor per
        // `id` gesetzte Konto-Verknüpfung – Umami hat dafür keine "unidentify"-Funktion, siehe
        // `setAnalyticsOptOut` in diesem File und den Logout-Hinweis in den Einstellungen.
        window.umami?.identify({isLoggedIn: false});
    }, []);

    const addToLibrary = useCallback(async (walkId: string) => {
        const res = await fetch('/api/library/add', {
            body: JSON.stringify({walkId}),
            credentials: 'include',
            headers: {
                'Accept-Language': i18n.language,
                'Content-Type': 'application/json'
            },
            method: 'POST'
        });

        if (!res.ok) {
            const data = await res.json() as {message?: string};

            throw new Error(data.message ?? t('account.addToLibraryError'));
        }

        window.umami?.track('walk_added_to_library', {walkId});
        await refreshUser();
    }, [i18n.language, refreshUser, t]);

    const removeFromLibrary = useCallback(async (walkId: string) => {
        const res = await fetch('/api/library/remove', {
            body: JSON.stringify({walkId}),
            credentials: 'include',
            headers: {
                'Accept-Language': i18n.language,
                'Content-Type': 'application/json'
            },
            method: 'POST'
        });

        if (!res.ok) {
            const data = await res.json() as {message?: string};

            throw new Error(data.message ?? t('account.removeFromLibraryError'));
        }

        await refreshUser();
    }, [i18n.language, refreshUser, t]);

    const isInLibrary = useCallback((walkId: string): boolean => {
        if (!user) return false;

        return getLibraryIds(user.library).includes(walkId);
    }, [user]);

    const setAnalyticsOptOut = useCallback(async (optOut: boolean) => {
        if (!user) return;

        const res = await fetch(`/api/users/${user.id}`, {
            body: JSON.stringify({analyticsOptOut: optOut}),
            credentials: 'include',
            headers: {'Content-Type': 'application/json'},
            method: 'PATCH'
        });

        if (!res.ok) {
            throw new Error(t('settings.analyticsLinkingError'));
        }

        await refreshUser();
    }, [refreshUser, t, user]);

    const updateName = useCallback(async (name: string) => {
        if (!user) return;

        const res = await fetch(`/api/users/${user.id}`, {
            body: JSON.stringify({name}),
            credentials: 'include',
            headers: {'Content-Type': 'application/json'},
            method: 'PATCH'
        });

        if (!res.ok) {
            const data = await res.json() as PayloadErrorResponse;

            throw new Error(extractPayloadError(data) ?? t('account.nameUpdateError'));
        }

        await refreshUser();
    }, [refreshUser, t, user]);

    return useMemo<AuthContextValue>(() => ({
        addToLibrary,
        authCheckFailed,
        isAdmin,
        isAuthenticated: user !== null,
        isInLibrary,
        isLoading,
        login,
        logout,
        refreshUser,
        register,
        removeFromLibrary,
        setAnalyticsOptOut,
        updateName,
        user
    }), [
        addToLibrary,
        authCheckFailed,
        isAdmin,
        isInLibrary,
        isLoading,
        login,
        logout,
        refreshUser,
        register,
        removeFromLibrary,
        setAnalyticsOptOut,
        updateName,
        user
    ]);
};

/**
 * AuthProvider stellt den Authentifizierungs-Kontext über Payload CMS bereit.
 * Nutzt die eingebauten Payload REST-Endpunkte mit HTTP-only Cookie-Authentifizierung.
 *
 * @param props          Die Komponenten-Props.
 * @param props.children Die Kind-Elemente.
 * @returns ReactNode mit dem AuthProvider.
 */
const AuthProvider = ({children}: {children: ReactNode}) => {
    const value = useAuthProvider();

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

AuthProvider.displayName = 'AuthProvider';

/**
 * Hook für den Zugriff auf den Authentifizierungs-Kontext.
 * Muss innerhalb eines `AuthProvider` verwendet werden.
 *
 * @returns Der AuthContext-Wert mit User-State und Auth-Methoden.
 * @throws Error wenn der Hook außerhalb eines AuthProvider verwendet wird.
 */
const useAuth = (): AuthContextValue => {
    const context = useContext(AuthContext);

    if (!context) {
        throw new Error('useAuth muss innerhalb eines AuthProvider verwendet werden.');
    }

    return context;
};

export {AuthProvider, useAuth};
export type {AuthContextValue};